// frontend/src/pages/ChecklistManager.js - 完全版（CSV読み込み + 手動編集）
import React, { useState, useEffect } from "react";
import { useOrganization } from "../contexts/OrganizationContext";
import { supabase } from "../supabase";
import Header from "../components/ui/Header";

const ChecklistManager = () => {
  const { currentOrganization } = useOrganization();
  const [procedures, setProcedures] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedProcedure, setSelectedProcedure] = useState("");
  const [selectedRole, setSelectedRole] = useState("scrub");
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  
  // 手動編集用のstate
  const [editMode, setEditMode] = useState(false);
  const [currentChecklist, setCurrentChecklist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  // 術式一覧取得
  useEffect(() => {
    const fetchProcedures = async () => {
      if (!currentOrganization?.id) return;
      
      try {
        const { data: deptData, error: deptError } = await supabase
          .from("departments")
          .select("*")
          .eq("organization_id", currentOrganization.id);

        if (deptError) throw deptError;
        setDepartments(deptData || []);

        const { data: procData, error: procError } = await supabase
          .from("procedures")
          .select("*");

        if (procError) throw procError;

        const proceduresWithDept = procData.map(proc => {
          const dept = deptData.find(d => d.id === proc.department_id);
          return {
            ...proc,
            department_name: dept?.name || "不明"
          };
        });

        console.log("📋 Fetched procedures:", proceduresWithDept);
        setProcedures(proceduresWithDept);
      } catch (error) {
        console.error("❌ 術式の取得に失敗:", error);
        showNotification("術式の取得に失敗しました", "error");
      }
    };

    fetchProcedures();
  }, [currentOrganization?.id]);

  // 診療科が変更されたら術式選択をリセット
  useEffect(() => {
    setSelectedProcedure("");
    setEditMode(false);
    setCurrentChecklist([]);
  }, [selectedDepartment]);

  // 選択された診療科に属する術式のみフィルタリング
  const filteredProcedures = selectedDepartment
    ? procedures.filter(proc => proc.department_id === selectedDepartment)
    : [];

  // CSVファイル選択
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "text/csv") {
      setCsvFile(file);
    } else {
      showNotification("CSVファイルを選択してください", "error");
    }
  };

  const readFileWithEncoding = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        const uint8Array = new Uint8Array(arrayBuffer);
        
        // UTF-8チェック（BOM付き）
        if (uint8Array.length >= 3 && 
            uint8Array[0] === 0xEF && 
            uint8Array[1] === 0xBB && 
            uint8Array[2] === 0xBF) {
          console.log("📄 UTF-8 with BOM detected");
          const decoder = new TextDecoder('utf-8');
          const text = decoder.decode(uint8Array.slice(3));
          resolve(text);
          return;
        }
        
        // Shift-JISを試す
        try {
          const decoder = new TextDecoder('shift-jis');
          const text = decoder.decode(uint8Array);
          
          // 日本語が含まれているか簡易チェック
          if (/[ぁ-ん]|[ァ-ヴ]|[一-龯]/.test(text)) {
            console.log("📄 Shift-JIS detected (日本語含む)");
            resolve(text);
            return;
          }
        } catch (error) {
          console.warn("Shift-JIS decode failed, trying UTF-8");
        }
        
        // デフォルトはUTF-8
        try {
          const decoder = new TextDecoder('utf-8');
          const text = decoder.decode(uint8Array);
          console.log("📄 UTF-8 detected (default)");
          resolve(text);
        } catch (error) {
          reject(new Error('文字エンコーディングの検出に失敗しました'));
        }
      };
      
      reader.onerror = () => reject(new Error('ファイルの読み込みに失敗しました'));
      reader.readAsArrayBuffer(file);
    });
  };

  // CSVパース処理
  const parseCSV = (text) => {
    const lines = text.split("\n").filter(line => line.trim());
    const headers = lines[0].split(",");
    
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",");
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((header, index) => {
          row[header.trim()] = values[index].trim();
        });
        data.push(row);
      }
    }
    
    return data;
  };

  // CSVをチェックリスト形式に変換
  const convertToChecklistFormat = (csvData, procedureId) => {
    const checklistsByRole = {};
    
    csvData.forEach(row => {
      const role = row.role;
      const category = row.category;
      const itemText = row.item_text;
      
      if (!checklistsByRole[role]) {
        checklistsByRole[role] = {};
      }
      
      if (!checklistsByRole[role][category]) {
        checklistsByRole[role][category] = [];
      }
      
      checklistsByRole[role][category].push({
        id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text: itemText
      });
    });
    
    const result = {};
    Object.keys(checklistsByRole).forEach(role => {
      result[role] = Object.keys(checklistsByRole[role]).map(category => ({
        category: category,
        items: checklistsByRole[role][category]
      }));
    });
    
    return result;
  };

  // CSV一括登録
  const handleImport = async () => {
    if (!csvFile) {
      showNotification("CSVファイルを選択してください", "error");
      return;
    }

    if (!selectedDepartment) {
      showNotification("診療科を選択してください", "error");
      return;
    }

    if (!selectedProcedure) {
      showNotification("術式を選択してください", "error");
      return;
    }

    setImporting(true);
    
    try {
        const text = await readFileWithEncoding(csvFile);
    
        console.log("📊 File content preview:", text.substring(0, 200));
        
      const csvData = parseCSV(text);
      
      if (csvData.length === 0) {
        showNotification("CSVファイルにデータがありません", "error");
        setImporting(false);
        return;
      }

      console.log("📊 Parsed CSV data:", csvData);

      const checklistsByRole = convertToChecklistFormat(csvData, selectedProcedure);
      
      console.log("📋 Converted checklists:", checklistsByRole);

      const savePromises = Object.keys(checklistsByRole).map(async (role) => {
        const { data: existing, error: fetchError } = await supabase
          .from('procedure_checklists')
          .select('id')
          .eq('procedure_id', selectedProcedure)
          .eq('organization_id', currentOrganization.id)
          .eq('role', role)
          .maybeSingle();

        if (fetchError) {
          console.error("❌ 既存データ確認エラー:", fetchError);
          throw fetchError;
        }

        if (existing) {
          console.log(`🔄 既存データを更新: ${role}`);
          return supabase
            .from('procedure_checklists')
            .update({
              checklist_items: checklistsByRole[role],
              updated_at: new Date().toISOString()
            })
            .eq('id', existing.id);
        } else {
          console.log(`➕ 新規データを挿入: ${role}`);
          return supabase
            .from('procedure_checklists')
            .insert({
              procedure_id: selectedProcedure,
              organization_id: currentOrganization.id,
              role: role,
              checklist_items: checklistsByRole[role],
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
        }
      });

      const results = await Promise.all(savePromises);
      
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error("❌ 保存エラー:", errors);
        throw new Error("一部のチェックリストの保存に失敗しました");
      }

      showNotification(`チェックリストを${Object.keys(checklistsByRole).length}件登録しました`, "success");
      setCsvFile(null);
      
      document.getElementById("csv-file-input").value = "";

    } catch (error) {
      console.error("❌ CSV読み込みエラー:", error);
      showNotification("CSV読み込みに失敗しました", "error");
    } finally {
      setImporting(false);
    }
  };

  // 既存チェックリストを読み込む
  const loadChecklist = async () => {
    if (!selectedProcedure || !selectedRole) {
      showNotification("術式と役割を選択してください", "error");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('procedure_checklists')
        .select('checklist_items')
        .eq('procedure_id', selectedProcedure)
        .eq('organization_id', currentOrganization.id)
        .eq('role', selectedRole)
        .maybeSingle();

      if (error) throw error;

      if (data && data.checklist_items) {
        setCurrentChecklist(data.checklist_items);
        setEditMode(true);
        setHasChanges(false);
        showNotification("チェックリストを読み込みました", "success");
      } else {
        // 新規作成
        setCurrentChecklist([]);
        setEditMode(true);
        setHasChanges(false);
        showNotification("新規チェックリストを作成します", "info");
      }
    } catch (error) {
      console.error("❌ チェックリスト読み込みエラー:", error);
      showNotification("チェックリストの読み込みに失敗しました", "error");
    } finally {
      setLoading(false);
    }
  };

  // カテゴリを追加
  const addCategory = () => {
    const categoryName = prompt("カテゴリ名を入力してください:");
    if (categoryName && categoryName.trim()) {
      setCurrentChecklist([...currentChecklist, {
        category: categoryName.trim(),
        items: []
      }]);
      setHasChanges(true);
    }
  };

  // カテゴリを削除
  const deleteCategory = (index) => {
    if (window.confirm("このカテゴリを削除しますか？")) {
      const updated = [...currentChecklist];
      updated.splice(index, 1);
      setCurrentChecklist(updated);
      setHasChanges(true);
    }
  };

  // カテゴリ名を編集
  const editCategoryName = (index) => {
    const newName = prompt("新しいカテゴリ名を入力してください:", currentChecklist[index].category);
    if (newName && newName.trim()) {
      const updated = [...currentChecklist];
      updated[index].category = newName.trim();
      setCurrentChecklist(updated);
      setHasChanges(true);
    }
  };

  // 項目を追加
  const addItem = (categoryIndex) => {
    const itemText = prompt("項目のテキストを入力してください:");
    if (itemText && itemText.trim()) {
      const updated = [...currentChecklist];
      const newItem = {
        id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        text: itemText.trim()
      };
      updated[categoryIndex].items.push(newItem);
      setCurrentChecklist(updated);
      setHasChanges(true);
    }
  };

  // 項目を編集
  const editItem = (categoryIndex, itemIndex) => {
    const currentText = currentChecklist[categoryIndex].items[itemIndex].text;
    const newText = prompt("項目のテキストを編集してください:", currentText);
    if (newText && newText.trim()) {
      const updated = [...currentChecklist];
      updated[categoryIndex].items[itemIndex].text = newText.trim();
      setCurrentChecklist(updated);
      setHasChanges(true);
    }
  };

  // 項目を削除
  const deleteItem = (categoryIndex, itemIndex) => {
    if (window.confirm("この項目を削除しますか？")) {
      const updated = [...currentChecklist];
      updated[categoryIndex].items.splice(itemIndex, 1);
      setCurrentChecklist(updated);
      setHasChanges(true);
    }
  };

  // チェックリストを保存
  const saveChecklist = async () => {
    if (!selectedProcedure || !selectedRole) {
      showNotification("術式と役割を選択してください", "error");
      return;
    }

    setLoading(true);
    try {
      const { data: existing, error: fetchError } = await supabase
        .from('procedure_checklists')
        .select('id')
        .eq('procedure_id', selectedProcedure)
        .eq('organization_id', currentOrganization.id)
        .eq('role', selectedRole)
        .maybeSingle();

      if (fetchError) throw fetchError;

      if (existing) {
        // 更新
        const { error: updateError } = await supabase
          .from('procedure_checklists')
          .update({
            checklist_items: currentChecklist,
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (updateError) throw updateError;
        showNotification("チェックリストを更新しました", "success");
      } else {
        // 新規挿入
        const { error: insertError } = await supabase
          .from('procedure_checklists')
          .insert({
            procedure_id: selectedProcedure,
            organization_id: currentOrganization.id,
            role: selectedRole,
            checklist_items: currentChecklist,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (insertError) throw insertError;
        showNotification("チェックリストを保存しました", "success");
      }

      setHasChanges(false);
    } catch (error) {
      console.error("❌ 保存エラー:", error);
      showNotification("保存に失敗しました", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!currentOrganization?.id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-700">組織を選択してください</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-8">
      {notification.show && (
        <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
          notification.type === "error" ? "bg-red-500" : 
          notification.type === "info" ? "bg-blue-500" : "bg-teal-500"
        } text-white`}>
          {notification.message}
        </div>
      )}

      <Header />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="bg-white rounded-xl shadow-md p-8 mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">
            チェックリスト管理
          </h1>

          {/* CSV読み込みセクション */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              📄 CSV一括読み込み
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  診療科を選択 {departments.length > 0 && `(${departments.length}件)`}
                </label>
                <select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="">
                    {departments.length === 0 
                      ? "-- 診療科データを読み込み中... --" 
                      : "-- 診療科を選択してください --"}
                  </option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  術式を選択 
                  {selectedDepartment && filteredProcedures.length > 0 && ` (${filteredProcedures.length}件)`}
                </label>
                <select
                  value={selectedProcedure}
                  onChange={(e) => setSelectedProcedure(e.target.value)}
                  disabled={!selectedDepartment}
                  className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 ${
                    !selectedDepartment ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="">
                    {!selectedDepartment 
                      ? "-- まず診療科を選択してください --"
                      : filteredProcedures.length === 0 
                        ? "-- この診療科に術式がありません --" 
                        : "-- 術式を選択してください --"}
                  </option>
                  {filteredProcedures.map((proc) => (
                    <option key={proc.doc_id} value={proc.doc_id}>
                      {proc.name}
                    </option>
                  ))}
                </select>
                {!selectedDepartment && (
                  <p className="mt-2 text-sm text-gray-500">
                    💡 診療科を選択すると、その診療科の術式が表示されます
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CSVファイル
                </label>
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
                {csvFile && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ {csvFile.name} を選択中
                  </p>
                )}
              </div>

              <button
                onClick={handleImport}
                disabled={importing || !csvFile || !selectedProcedure || !selectedDepartment}
                className={`w-full py-3 px-6 rounded-lg font-medium transition duration-150 ${
                  importing || !csvFile || !selectedProcedure || !selectedDepartment
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-teal-600 text-white hover:bg-teal-700"
                }`}
              >
                {importing ? "読み込み中..." : "CSVを読み込む"}
              </button>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                📝 CSVフォーマット
              </h3>
              <pre className="text-xs text-gray-600 overflow-x-auto">
{`procedure_name,department,role,category,item_text
ゲル充填,整形外科,scrub,基礎知識,器械の名称を理解している
ゲル充填,整形外科,scrub,基礎知識,器械の配置を理解している
ゲル充填,整形外科,circulating,患者対応,患者確認を正確に行える`}
              </pre>
              <p className="text-xs text-gray-500 mt-2">
                ※ role: scrub（器械出し）/ circulating（外回り）
              </p>
            </div>
          </div>

          {/* 手動編集セクション */}
          <div className="border-2 border-solid border-teal-300 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              ✏️ 手動編集
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    診療科
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">-- 診療科を選択 --</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    術式
                  </label>
                  <select
                    value={selectedProcedure}
                    onChange={(e) => {
                      setSelectedProcedure(e.target.value);
                      setEditMode(false);
                      setCurrentChecklist([]);
                    }}
                    disabled={!selectedDepartment}
                    className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 ${
                      !selectedDepartment ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  >
                    <option value="">-- 術式を選択 --</option>
                    {filteredProcedures.map((proc) => (
                      <option key={proc.doc_id} value={proc.doc_id}>
                        {proc.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    役割
                  </label>
                  <select
                    value={selectedRole}
                    onChange={(e) => {
                      setSelectedRole(e.target.value);
                      setEditMode(false);
                      setCurrentChecklist([]);
                    }}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="scrub">器械出し</option>
                    <option value="circulating">外回り</option>
                  </select>
                </div>
              </div>

              <button
                onClick={loadChecklist}
                disabled={!selectedProcedure || loading}
                className={`w-full py-3 px-6 rounded-lg font-medium transition duration-150 ${
                  !selectedProcedure || loading
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {loading ? "読み込み中..." : "チェックリストを読み込む"}
              </button>

              {/* 編集エリア */}
              {editMode && (
                <div className="mt-6 border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-md font-semibold text-gray-800">
                      チェックリスト編集
                      {hasChanges && <span className="ml-2 text-amber-600">●未保存</span>}
                    </h3>
                    <div className="space-x-2">
                      <button
                        onClick={addCategory}
                        className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
                      >
                        ➕ カテゴリ追加
                      </button>
                      <button
                        onClick={saveChecklist}
                        disabled={!hasChanges || loading}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                          !hasChanges || loading
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-green-600 hover:bg-green-700 text-white"
                        }`}
                      >
                        {loading ? "保存中..." : "💾 保存"}
                      </button>
                    </div>
                  </div>

                  {currentChecklist.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>カテゴリがありません。「カテゴリ追加」ボタンから追加してください。</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {currentChecklist.map((category, catIndex) => (
                        <div key={catIndex} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex justify-between items-center mb-3">
                            <h4 className="font-semibold text-gray-800">
                              【{category.category}】
                            </h4>
                            <div className="space-x-2">
                              <button
                                onClick={() => editCategoryName(catIndex)}
                                className="text-blue-600 hover:text-blue-800 text-sm"
                              >
                                ✏️ 編集
                              </button>
                              <button
                                onClick={() => addItem(catIndex)}
                                className="text-teal-600 hover:text-teal-800 text-sm"
                              >
                                ➕ 項目追加
                              </button>
                              <button
                                onClick={() => deleteCategory(catIndex)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                🗑️ 削除
                              </button>
                            </div>
                          </div>

                          {category.items.length === 0 ? (
                            <p className="text-sm text-gray-500 italic">項目がありません</p>
                          ) : (
                            <ul className="space-y-2">
                              {category.items.map((item, itemIndex) => (
                                <li key={item.id} className="flex justify-between items-center bg-white p-3 rounded">
                                  <span className="text-sm text-gray-700">{item.text}</span>
                                  <div className="space-x-2">
                                    <button
                                      onClick={() => editItem(catIndex, itemIndex)}
                                      className="text-blue-600 hover:text-blue-800 text-xs"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      onClick={() => deleteItem(catIndex, itemIndex)}
                                      className="text-red-600 hover:text-red-800 text-xs"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChecklistManager;